// src/utils/image-utils.ts
export const DEFAULT_WIDTHS = [320, 480, 768, 1024, 1600] as const
export type Widths = typeof DEFAULT_WIDTHS[number][]

/**
 * Split a URL into { dir, name, ext } or return null if it doesn't look like an image.
 * Examples:
 *  "/uploads/img.jpg"  -> { dir: "/uploads", name: "img", ext: ".jpg" }
 *  "https://..." -> allowed but path parts will be taken (not recommended for local resized files)
 */
export function splitFilename(url: string) {
  const withoutQuery = url.split('?')[0]
  const parts = withoutQuery.split('/')
  const base = parts.pop() || ''
  const dir = parts.length ? ('/' + parts.join('/')) : ''
  const m = base.match(/^(.+)\.(jpe?g|png|webp|avif)$/i)
  if (!m) return null
  const name = m[1]
  const ext = '.' + m[2].toLowerCase()
  return { dir, base, name, ext }
}

/**
 * Build the resized filename for a given width & extension.
 * name='img', width=320, ext='.jpg' => 'img-320.jpg'
 */
export function buildResizedFileName(name: string, width: number, ext: string) {
  return `${name}-${width}${ext}`
}

/**
 * Build a resized URL given an original URL and the resizedBasePath.
 * E.g. original "/uploads/img.jpg", width=320, ext=".webp", resizedBasePath="/uploads/resized"
 * -> "/uploads/resized/img-320.webp"
 */
export function buildResizedUrl(originalUrl: string, width: number, ext: string, resizedBasePath = '/uploads/resized') {
  const parts = splitFilename(originalUrl)
  if (!parts) return originalUrl
  const file = buildResizedFileName(parts.name, width, ext)
  // Ensure leading slash and no doubled slashes
  return `${resizedBasePath.replace(/\/+$/, '')}/${file}`
}

/**
 * Build a srcset string for the given format extension ('.avif' '.webp' '.jpg')
 */
export function buildSrcSet(originalUrl: string, widths: number[] = Array.from(DEFAULT_WIDTHS), resizedBasePath = '/uploads/resized', ext = '.jpg') {
  const parts = splitFilename(originalUrl)
  if (!parts) return ''
  return widths.map((w) => `${buildResizedUrl(originalUrl, w, ext, resizedBasePath)} ${w}w`).join(', ')
}